import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"
import { showConfirmModal, showAlertModal } from "../lib/custom_confirm"

// Status quick-actions inside the appointment detail modal. Replaces the
// old inline updateAppointmentStatus() on the deleted standalone page.
// On success it reloads the detail frame (fresh status badge) and emits
// `appointment:changed` so the calendar can refetch its events — no full
// page reload.
const GENERIC_ERROR = "Error updating appointment status"

export default class extends Controller {
  static values = { url: String }

  async update(event) {
    const status = event.params.status
    const confirmed = await showConfirmModal("Are you sure you want to update this appointment status?")
    if (!confirmed) return

    try {
      const response = await patch(this.urlValue, {
        body: JSON.stringify({ appointment: { status } }),
        responseKind: "json",
      })
      // Read the body on failure too: handle_failed_update answers 422
      // { errors: [...] } with the model's actual reason.
      const data = await this.responseData(response)

      if (response.ok && data && data.success) {
        this.announceSuccess(data)
      } else {
        this.announceError(this.errorMessage(data))
      }
    } catch {
      this.announceError()
    }
  }

  async responseData(response) {
    try {
      return await response.json
    } catch {
      return null // non-JSON error page (a 500, a login redirect)
    }
  }

  errorMessage(data) {
    const errors = Array.isArray(data?.errors) ? data.errors.filter((e) => typeof e === "string" && e) : []
    if (errors.length > 0) return errors.join(", ")
    if (typeof data?.message === "string" && data.message) return data.message
    return GENERIC_ERROR
  }

  announceSuccess(data) {
    if (typeof window.showToast === "function" && data.message) {
      window.showToast(data.message, "bg-success")
    }
    if (typeof window.showToast === "function" && data.warning) {
      setTimeout(() => window.showToast(data.warning, "bg-warning"), 500)
    }
    window.dispatchEvent(new CustomEvent("appointment:changed"))
    const frame = document.getElementById("appointment_detail")
    if (frame && typeof frame.reload === "function") frame.reload()
  }

  announceError(message = GENERIC_ERROR) {
    if (typeof window.showToast === "function") {
      window.showToast(message, "bg-danger")
    } else {
      showAlertModal(message, { variant: "danger", title: "Error" })
    }
  }
}
